import * as React from "react";
/**
 * Turns any element into a file drop target.
 *
 * Passing no handler yields NO handlers at all, which is the point: a surface
 * that cannot take files must not swallow the browser's own drop behaviour nor
 * advertise a target it will ignore.
 */ const NO_DROP_TARGET = {
    isDraggingOver: false,
    dropHandlers: {}
};
export function useFileDrop(onFilesDropped, /**
   * Whether the target is taking files RIGHT NOW. False keeps the element a
   * drop target that simply swallows the drop — no hint, no files — which is a
   * different thing from having no target at all.
   *
   * The distinction is the bug it exists to stop: a busy surface that passed
   * `undefined` got the empty handler set, so nothing called `preventDefault`
   * and the browser's own behaviour took over — dropping a PDF onto a chat that
   * was mid-answer NAVIGATED THE TAB to the file and threw the user's draft
   * away. "Not right now" has to be said, not left unsaid.
   */ accepting = true) {
    const [isDraggingOver, setIsDraggingOver] = React.useState(false);
    const onDragOver = React.useCallback((event)=>{
        event.preventDefault();
        if (accepting) {
            setIsDraggingOver(true);
            return;
        }
        // Taking the event over without this leaves the OS cursor showing a copy
        // affordance the drop will not honour. Refusing has to look like refusing.
        if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
    }, [
        accepting
    ]);
    // Dragging across a child fires `dragleave` on the parent, so the flag is
    // only cleared once the pointer has truly left the target.
    const onDragLeave = React.useCallback((event)=>{
        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsDraggingOver(false);
        }
    }, []);
    const onDrop = React.useCallback((event)=>{
        event.preventDefault();
        setIsDraggingOver(false);
        if (!accepting) return;
        onFilesDropped?.(Array.from(event.dataTransfer?.files ?? []));
    }, [
        accepting,
        onFilesDropped
    ]);
    // Memoised because the panel that spreads these re-renders on every streamed
    // token: a fresh handler object per token re-attaches three listeners.
    const result = React.useMemo(()=>({
            isDraggingOver,
            dropHandlers: {
                onDragOver,
                onDragLeave,
                onDrop
            }
        }), [
        isDraggingOver,
        onDragOver,
        onDragLeave,
        onDrop
    ]);
    return onFilesDropped ? result : NO_DROP_TARGET;
}

//# sourceMappingURL=useFileDrop.js.map